import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader2, UserPlus, Check, X, Mail, Phone, Building, Clock, Trash2 } from "lucide-react";
import type { UserProfile } from "@shared/schema";

interface UserWithProfile {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  profile?: UserProfile;
}

interface AccessRequest {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  company?: string;
  message?: string;
  status: "pending" | "approved" | "denied";
  assignedRole?: string;
  processedAt?: string;
  createdAt: string;
}

interface AccessRequestsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ROLE_OPTIONS = [
  { value: "sales_rep", label: "Sales Rep" },
  { value: "artist", label: "Talent" },
  { value: "billing", label: "Billing" },
  { value: "admin", label: "Admin" },
  // v2: { value: "marketing", label: "Marketing" },
];

export function AccessRequestsDialog({ open, onOpenChange }: AccessRequestsDialogProps) {
  const { toast } = useToast();
  const [selectedRole, setSelectedRole] = useState<Record<string, string>>({});

  const { data: requests = [], isLoading } = useQuery<AccessRequest[]>({
    queryKey: ["/api/admin/access-requests"],
    enabled: open,
  });

  const approveMutation = useMutation({
    mutationFn: async ({ id, role }: { id: string; role: string }) => {
      const response = await apiRequest("POST", `/api/admin/access-requests/${id}/approve`, { role });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/access-requests"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      toast({
        title: "Request Approved",
        description: "User has been created and notified via email.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to approve request.",
        variant: "destructive",
      });
    },
  });

  const denyMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await apiRequest("POST", `/api/admin/access-requests/${id}/deny`);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/access-requests"] });
      toast({
        title: "Request Denied",
        description: "The access request has been denied.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to deny request.",
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await apiRequest("DELETE", `/api/admin/access-requests/${id}`);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/access-requests"] });
      toast({
        title: "Request Deleted",
        description: "The denied access request has been removed.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete request.",
        variant: "destructive",
      });
    },
  });

  const pendingRequests = requests.filter(r => r.status === "pending");
  const processedRequests = requests.filter(r => r.status !== "pending");

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "pending":
        return <Badge variant="outline" className="bg-yellow-500/10 text-yellow-600 border-yellow-500/30">Pending</Badge>;
      case "approved":
        return <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/30">Approved</Badge>;
      case "denied":
        return <Badge variant="outline" className="bg-red-500/10 text-red-600 border-red-500/30">Denied</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full sm:max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            Access Requests
          </DialogTitle>
          <DialogDescription>
            Review and manage user access requests.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-auto mt-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : requests.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <UserPlus className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No access requests yet.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {pendingRequests.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Pending Requests ({pendingRequests.length})
                  </h3>
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Company</TableHead>
                          <TableHead>Submitted</TableHead>
                          <TableHead>Assign Role</TableHead>
                          <TableHead className="w-[120px]">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {pendingRequests.map((request) => (
                          <TableRow key={request.id}>
                            <TableCell>
                              <div>
                                <div className="font-medium" data-testid={`text-name-${request.id}`}>
                                  {request.firstName} {request.lastName}
                                </div>
                                {request.phone && (
                                  <div className="text-xs text-muted-foreground flex items-center gap-1">
                                    <Phone className="w-3 h-3" />
                                    <span data-testid={`text-phone-${request.id}`}>{request.phone}</span>
                                  </div>
                                )}
                                {request.message && (
                                  <div className="text-xs text-muted-foreground mt-1 italic max-w-[200px] truncate" title={request.message} data-testid={`text-message-${request.id}`}>
                                    "{request.message}"
                                  </div>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1 text-sm" data-testid={`text-email-${request.id}`}>
                                <Mail className="w-3 h-3 text-muted-foreground" />
                                {request.email}
                              </div>
                            </TableCell>
                            <TableCell>
                              {request.company ? (
                                <div className="flex items-center gap-1 text-sm" data-testid={`text-company-${request.id}`}>
                                  <Building className="w-3 h-3 text-muted-foreground" />
                                  {request.company}
                                </div>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground" data-testid={`text-date-${request.id}`}>
                              {formatDate(request.createdAt)}
                            </TableCell>
                            <TableCell>
                              <Select
                                value={selectedRole[request.id] || "sales_rep"}
                                onValueChange={(value) => setSelectedRole(prev => ({ ...prev, [request.id]: value }))}
                              >
                                <SelectTrigger className="w-[130px]" data-testid={`select-role-${request.id}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {ROLE_OPTIONS.map((option) => (
                                    <SelectItem key={option.value} value={option.value}>
                                      {option.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  onClick={() => approveMutation.mutate({ 
                                    id: request.id, 
                                    role: selectedRole[request.id] || "sales_rep" 
                                  })}
                                  disabled={approveMutation.isPending}
                                  title="Approve"
                                  data-testid={`button-approve-${request.id}`}
                                >
                                  <Check className="w-4 h-4 text-green-600" />
                                </Button>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  onClick={() => denyMutation.mutate(request.id)}
                                  disabled={denyMutation.isPending}
                                  title="Deny"
                                  data-testid={`button-deny-${request.id}`}
                                >
                                  <X className="w-4 h-4 text-red-600" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {processedRequests.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-3 text-muted-foreground">
                    Previously Processed ({processedRequests.length})
                  </h3>
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Role</TableHead>
                          <TableHead>Processed</TableHead>
                          <TableHead className="w-[60px]">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {processedRequests.map((request) => (
                          <TableRow key={request.id} className="opacity-70">
                            <TableCell className="font-medium">
                              {request.firstName} {request.lastName}
                            </TableCell>
                            <TableCell className="text-sm">
                              {request.email}
                            </TableCell>
                            <TableCell>
                              {getStatusBadge(request.status)}
                            </TableCell>
                            <TableCell className="text-sm">
                              {request.assignedRole ? (
                                <Badge variant="secondary">
                                  {ROLE_OPTIONS.find(r => r.value === request.assignedRole)?.label || request.assignedRole}
                                </Badge>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {request.processedAt ? formatDate(request.processedAt) : "—"}
                            </TableCell>
                            <TableCell>
                              {request.status === "denied" && (
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  onClick={() => deleteMutation.mutate(request.id)}
                                  disabled={deleteMutation.isPending}
                                  title="Delete denied request"
                                  data-testid={`button-delete-${request.id}`}
                                >
                                  <Trash2 className="w-4 h-4 text-muted-foreground hover:text-destructive" />
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
